import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    TextField,
    Button,
} from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { parentUrl } from '../../../../utils/constants';
import { localDateFormat } from '../../../../utils/timeFormat';
import WidgetLoading from '../../Shared/WidgetLoading';
import { copyObject } from '../../../../utils/common';
import Authorization from '../../../../utils/Authorization';
import { TimeFilterEnum } from '../../../../Enum/TimeFilterEnum';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import b64toBlob from 'b64-to-blob';
import { overrideAppointmentDocumentationStatus } from '../../../../store/appointment.slice';
import { fetchAppointmentsNotDocumentedWidget, exportAppointmentsNotDocumentedWidget } from '../../../Widgets/WidgetService';

const AppointmentsNotDocumented = () => {
    const authData = new Authorization();
    const dispatch = useAppDispatch();
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const initialState = () => {
        return { Appointments: [], NumOfAppointments: 0, NumOfEncounters: 0 };
    };
    const [appointmentsNotDocumented, setAppointmentsNotDocumented] = useState(initialState());
    const [providerId, setProviderId] = useState(authData.UserId || '');
    const [selectedFilter, setSelectedFilter] = useState<TimeFilterEnum>(TimeFilterEnum.Last30Days);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [showInActiveProvider, setShowInActiveProvider] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const copyProviders = copyObject(activeProviders);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const hasPermission = authData.isInPermission('Adjust Not Documented Appointments');
    const filteredProviders = (showInActiveProvider ? copyProviders : copyProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const [selectionModel, setSelectionModel] = useState<string[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [documenting, setDocumenting] = useState(false);

    const allGreen = selectedRows.length > 0 && selectedRows.every((row) => row.HasEncounter);
    const allRed = selectedRows.length > 0 && selectedRows.every((row) => !row.HasEncounter);
    const mixedSelection = selectedRows.length > 0 && !(allGreen || allRed);

    let buttonText = 'Mark As Documented';
    if (allRed) buttonText = 'Mark As Documented';
    if (allGreen) buttonText = 'Mark As Not Documented';
    if (mixedSelection) buttonText = 'Select same type only';

    const handleClick = (event) => {
        setShow(!show);
    };

    const dayFilterList = [
        { Id: TimeFilterEnum.ThisWeek, Value: TimeFilterEnum.ThisWeekDescription },
        { Id: TimeFilterEnum.Last30Days, Value: TimeFilterEnum.Last30DaysDescription },
        { Id: TimeFilterEnum.Last60Days, Value: TimeFilterEnum.Last60DaysDescription },
        { Id: TimeFilterEnum.Custom, Value: TimeFilterEnum.CustomDescription },
    ];

    const columns: GridColDef[] = [
        {
            field: 'MemberName',
            headerName: 'Member',
            minWidth: 140,
            flex: 1,
        },
        {
            field: 'AppointmentDateTime',
            headerName: 'Appointment Date',
            minWidth: 130,
            flex: 1,
            valueGetter: (value, row) => `${localDateFormat(new Date(row.AppointmentDateTime))}`,
        },
        {
            field: 'AppointmentTypeString',
            headerName: 'Appointment Type',
            minWidth: 130,
            flex: 1,
        },
        {
            field: 'StatusString',
            headerName: 'Appointment Status',
            minWidth: 130,
            flex: 1,
        },
        {
            field: 'ProviderName',
            headerName: 'Provider Name',
            flex: 1,
            minWidth: 100,
        },
        {
            field: 'HasDraft',
            headerName: 'Has Draft',
            minWidth: 50,
            renderCell: (params) => {
                return <Typography variant="body2">{Boolean(params.row.HasDraft) ? 'Yes' : 'No'}</Typography>;
            },
        },
        {
            field: 'ClaimSubmitted',
            headerName: 'Claim Submitted',
            minWidth: 100,
            renderCell: (params) => {
                return <Typography variant="body2">{Boolean(params.row.ClaimSubmitted) ? 'Yes' : 'No'}</Typography>;
            },
        },
    ];

    const changeFilter = async () => {
        setIsLoading(true);
        let dateFrom = Boolean(fromDate) && selectedFilter === TimeFilterEnum.Custom ? new Date(fromDate).toISOString() : '';
        let dateTo = Boolean(toDate) && selectedFilter === TimeFilterEnum.Custom ? new Date(toDate).toISOString() : '';

        const response = await fetchAppointmentsNotDocumentedWidget(providerId, selectedFilter, dateFrom, dateTo);
        setAppointmentsNotDocumented(response);
        setIsLoading(false);
    };

    useEffect(() => {
        if (selectedFilter !== TimeFilterEnum.Custom || (Boolean(fromDate) && Boolean(toDate))) {
            setIsLoading(true);
            changeFilter();
        }

        if (selectedFilter !== TimeFilterEnum.Custom && Boolean(fromDate) && Boolean(toDate)) {
            setFromDate(null);
            setToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsLoading, selectedFilter, fromDate, toDate, providerId, showInActiveProvider]);

    const toggleInactiveHandler = (isChecked: boolean) => {
        setShowInActiveProvider(isChecked);
        setProviderId(authData.UserId);
    };

    const changeFromDate = (date: any) => {
        setFromDate(date);
    };

    const changeToDate = (date: any) => {
        setToDate(date);
    };

    const exportFuhfumAppointment = async () => {
        setDownLoading(true);
        let dateFrom = Boolean(fromDate) && selectedFilter === TimeFilterEnum.Custom ? new Date(fromDate).toISOString() : '';
        let dateTo = Boolean(toDate) && selectedFilter === TimeFilterEnum.Custom ? new Date(toDate).toISOString() : '';
        const response = await exportAppointmentsNotDocumentedWidget(providerId, selectedFilter, dateFrom, dateTo);
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `AppointmentsNotDocumented - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const updateAppointmentDocumentation = async () => {
        setDocumenting(true);
        const isDocument =
            selectedRows.length > 0 && selectedRows.every((row) => !row.HasEncounter)
                ? true // Red rows => Mark as Documented
                : false; // Green rows => Mark as Not Documented
        dispatch(
            overrideAppointmentDocumentationStatus(selectionModel, isDocument, (data) => {
                changeFilter();
                setSelectionModel([]);
                setSelectedRows([]);
                setDocumenting(false);
            })
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper
                    className="smartdashboard-widget"
                    elevation={5}
                    sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
                >
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} marginBottom={2}>
                            <Typography color={'primary'} variant="h6" textAlign={'center'} ref={ref}>
                                Appointments Not Documented (<span style={{ color: '#b71c1c' }}>{appointmentsNotDocumented?.NumOfAppointments || 0}</span>/
                                <span style={{ color: 'green' }}>{appointmentsNotDocumented?.NumOfEncounters || 0}</span>)
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="body2">Appointments has been documented within selected days</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Green :
                                            </Typography>
                                            <Typography variant="caption">
                                                Appointment has corresponding Encounter or marked as Not to be Encountered
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Red:
                                            </Typography>
                                            <Typography variant="caption">
                                                Appointments doesn't have encounter or not marked as Not to be Encountered
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={2.5}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="demo-simple-select-label">Provider</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={filteredProviders.find((el) => el.Id === providerId)?.Id || ''}
                                    label="Provider"
                                    size="small"
                                    onChange={(e) => {
                                        setProviderId(e.target.value);
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                                >
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={showInActiveProvider} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((provider) => {
                                        return (
                                            <MenuItem
                                                key={provider.Id}
                                                value={provider.Id}
                                                sx={{ color: provider.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}
                                            >
                                                {provider.LastName + ', ' + provider.FirstName}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={2.5}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={selectedFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSelectedFilter(parseInt(e.target.value as any))}
                                >
                                    {dayFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {selectedFilter === TimeFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 2.5 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(fromDate) ? fromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 2.5 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(toDate) ? toDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={2} display={'flex'} mb={2} maxHeight="55px">
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportFuhfumAppointment()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || appointmentsNotDocumented?.Appointments?.length === 0}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <>
                                <Grid size={{ md: 5 }}></Grid>
                                <Grid size={2} display={'flex'} mb={2} maxHeight="55px">
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportFuhfumAppointment()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || !Boolean(appointmentsNotDocumented?.Appointments?.length)}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </Grid>
                            </>
                        )}
                        <Grid size={12} position="relative">
                            {isLoading && <WidgetLoading />}
                            {hasPermission ? (
                                <Grid size={4} display={'flex'} mb={2} maxHeight="55px">
                                    <Button
                                        style={{ minWidth: '120px' }}
                                        onClick={() => updateAppointmentDocumentation()}
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                            documenting ||
                                            !Boolean(appointmentsNotDocumented?.Appointments?.length) ||
                                            mixedSelection ||
                                            selectedRows.length === 0
                                        }
                                    >
                                        {documenting ? 'Documenting..' : buttonText}
                                    </Button>
                                </Grid>
                            ) : null}
                            <div style={{ width: '100%', height: 'calc(100vh - 300px)', maxHeight: '420px' }}>
                                <DataGrid
                                    onCellClick={(params: any, event: React.MouseEvent) => {
                                        // Prevent checkbox click from triggering cell click action
                                        if (params.field === '__check__') {
                                            event.stopPropagation(); // Prevent default row click behavior
                                            return;
                                        }
                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/scheduling`, '_blank');
                                    }}
                                    rows={appointmentsNotDocumented?.Appointments || []}
                                    columns={columns}
                                    paginationModel={{ pageSize: pageSize, page: page }}
                                    pageSizeOptions={[5]}
                                    checkboxSelection={hasPermission}
                                    rowSelectionModel={
                                        Array.isArray(selectionModel) ? { type: 'include', ids: new Set(selectionModel) } : { type: 'include', ids: new Set() }
                                    }
                                    onRowSelectionModelChange={(newSelection) => {
                                        const selectionArray = Array.isArray(newSelection) ? newSelection : [];
                                        setSelectionModel(selectionArray);
                                        const selected = appointmentsNotDocumented?.Appointments?.filter((row) => selectionArray.includes(row.Id));
                                        setSelectedRows(selected || []);
                                    }}
                                    isRowSelectable={(params) => {
                                        return params.row?.OverrideDocumentationStatus === true || !params.row?.HasEncounter; // red = HasEncounter is false
                                    }}
                                    onPaginationModelChange={(model) => {
                                        setPageSize(model.pageSize);
                                        setPage(model.page);
                                    }}
                                    getRowId={(row) => row.Id}
                                    getRowHeight={() => 'auto'}
                                    getRowClassName={(params) => {
                                        return params.row.HasEncounter ? 'green' : 'red';
                                    }}
                                    sx={{
                                        [`& .${gridClasses.cell}`]: {
                                            py: 1,
                                        },
                                        [`& .green`]: {
                                            color: '#1b5e20',
                                        },
                                        [`& .red`]: {
                                            color: '#b71c1c',
                                        },
                                    }}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default AppointmentsNotDocumented;
